import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SectionHeader from '@/components/feature/SectionHeader';
import DramaCard from '@/components/feature/DramaCard';
import Pagination from '@/components/feature/Pagination';
import FilterPanel from './components/FilterPanel';
import { categories, dramas, regions, statuses, years } from '@/mocks/dramas';

const PAGE_SIZE = 8;

type SortKey = 'updated' | 'heat' | 'rating';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'updated', label: '按更新时间' },
  { key: 'heat', label: '按热度' },
  { key: 'rating', label: '按评分' },
];

export default function Category() {
  const { type = 'guochan' } = useParams();
  const current = categories.find((c) => c.slug === type) ?? categories[0];

  const baseList = useMemo(() => dramas.filter((d) => d.category === current.slug), [current.slug]);

  const [selected, setSelected] = useState<Record<string, (string | number)[]>>({
    regions: [],
    years: [],
    statuses: [],
  });
  const [sort, setSort] = useState<SortKey>('updated');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSelected({ regions: [], years: [], statuses: [] });
    setSort('updated');
    setPage(1);
  }, [type]);

  useEffect(() => {
    setLoading(true);
    const t = window.setTimeout(() => setLoading(false), 280);
    return () => window.clearTimeout(t);
  }, [type, selected, sort, page]);

  const filtered = useMemo(() => {
    let list = baseList.slice();
    if (selected.regions.length) list = list.filter((d) => selected.regions.includes(d.region));
    if (selected.years.length) list = list.filter((d) => selected.years.includes(d.year));
    if (selected.statuses.length) list = list.filter((d) => selected.statuses.includes(d.status));
    if (sort === 'heat') list.sort((a, b) => b.heat - a.heat);
    else if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
    else list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    return list;
  }, [baseList, selected, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageList = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggle = (key: 'regions' | 'years' | 'statuses', value: string | number) => {
    setPage(1);
    setSelected((prev) => {
      const arr = prev[key];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };
  const clear = () => {
    setSelected({ regions: [], years: [], statuses: [] });
    setPage(1);
  };

  const groups = [
    { key: 'regions' as const, label: '按地区', options: regions },
    { key: 'years' as const, label: '按年代', options: years },
    { key: 'statuses' as const, label: '按状态', options: statuses },
  ];

  const filterCount = Object.values(selected).reduce((sum, arr) => sum + arr.length, 0);
  const otherCats = categories.filter((c) => c.slug !== current.slug);

  return (
    <div className="w-full">
      {/* hero band */}
      <div className="w-full bg-primary-950">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-300" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span>剧集分类</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-accent-400">{current.name}</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
            {current.name} · 最新电视剧推荐与剧情介绍
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-background-300">{current.desc}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  c.slug === current.slug
                    ? 'bg-accent-500 font-semibold text-primary-950'
                    : 'bg-primary-900 text-background-200 hover:bg-primary-800'
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* sidebar filters (desktop) */}
          <aside className="hidden w-56 shrink-0 lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="mb-4 flex items-center gap-2">
                <i className="ri-filter-3-line text-lg text-primary-600" />
                <span className="font-heading text-base font-semibold text-foreground-950">筛选条件</span>
              </div>
              <FilterPanel groups={groups} selected={selected} onToggle={toggle} onClear={clear} />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            {/* toolbar */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共找到 <strong className="font-heading text-primary-600">{filtered.length}</strong> 部剧集
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="flex items-center gap-1 rounded-md border border-background-200 px-3 py-2 text-sm text-foreground-700 lg:hidden"
                >
                  <i className="ri-filter-3-line" />
                  筛选{filterCount > 0 ? `(${filterCount})` : ''}
                </button>
                <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
                  {sortOptions.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => {
                        setSort(o.key);
                        setPage(1);
                      }}
                      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors sm:text-sm ${
                        sort === o.key ? 'bg-background-50 font-semibold text-primary-600' : 'text-secondary-900'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* list */}
            {loading ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                  <div key={i} className="animate-pulse overflow-hidden rounded-lg border border-background-200">
                    <div className="aspect-[2/3] w-full bg-background-200" />
                    <div className="space-y-2 p-3">
                      <div className="h-4 w-3/4 rounded bg-background-200" />
                      <div className="h-3 w-1/2 rounded bg-background-200" />
                    </div>
                  </div>
                ))}
              </div>
            ) : pageList.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {pageList.map((d) => (
                    <DramaCard key={d.id} drama={d} />
                  ))}
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-emotion-sad-line text-2xl" />
                </span>
                <p className="mt-4 font-heading text-base font-semibold text-foreground-800">没有找到符合条件的剧集</p>
                <p className="mt-1 text-sm text-foreground-500">试试调整筛选条件，或浏览其他分类</p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={clear}
                    className="h-10 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    清除筛选条件
                  </button>
                  <Link
                    to="/"
                    className="flex h-10 items-center whitespace-nowrap rounded-md border border-background-300 px-5 text-sm text-foreground-700"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            )}

            {/* other categories */}
            <section className="mt-12">
              <SectionHeader title="其他分类" subtitle="继续探索更多类型的剧集" />
              <div className="flex flex-wrap gap-2">
                {otherCats.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="rounded-full border border-background-200 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* mobile filter drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-primary-950/50" onClick={() => setDrawerOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] animate-fade-up overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-heading text-base font-semibold text-foreground-950">筛选条件</span>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel groups={groups} selected={selected} onToggle={toggle} onClear={clear} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 h-11 w-full whitespace-nowrap rounded-md bg-primary-500 text-sm font-semibold text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}